(() => {
  function parseProfile(root) {
    const registry = window.SmagtronCalculatorRegistry.createRegistry(window.SmagtronCalculatorRegistry.catalog);
    const result = registry.resolve(
      { profileId: root.dataset.profileId },
      { allowedStatuses: ['pilot'] }
    );
    if (!result.ok) throw new Error(result.reason);
    const runtime = JSON.parse(root.querySelector('[data-calculator-runtime]').textContent);
    const configuredRuntime = Object.fromEntries(
      Object.entries(runtime).filter(([, value]) => value !== null)
    );
    return { ...result.profile, ...configuredRuntime };
  }

  function integer(value) {
    const number = Number(value);
    return Number.isInteger(number) ? number : null;
  }

  function valueInRange(root, selector, range, errors, key) {
    const value = Number(root.querySelector(selector)?.value);
    const scale = Math.pow(10, range.decimals || 0);
    const validStep = Number.isFinite(value) && Number.isInteger(value * scale) &&
      (!range.step || value % range.step === 0);
    if (!validStep || value < range.min || value > range.max) {
      errors[key] = `Bitte einen Wert zwischen ${range.min} und ${range.max} eingeben.`;
    }
    return value;
  }

  function calculate(root, profile) {
    const errors = {};
    const base = root.querySelector('[data-base-variant]')?.selectedOptions[0];
    const basePriceCents = profile.basePriceCents ?? integer(base?.dataset.priceCents);
    const quantity = valueInRange(root, '[data-quantity]', profile.quantity, errors, 'quantity');
    const selected = { baseVariantId: base?.value || '', quantity };
    let priceCents = 0;
    if (!profile.basePriceCents && (!base || basePriceCents === null)) {
      errors.baseVariantId = 'Bitte eine Grundvariante auswählen.';
    }

    if (profile.kind === 'length-quantity') {
      selected.lengthCm = valueInRange(root, '[data-length]', profile.length, errors, 'length');
      if (!Object.keys(errors).length) {
        priceCents = Math.round((basePriceCents + (profile.fixedSurchargeCents ?? 1790)) * (selected.lengthCm / 100) * quantity);
      }
    } else {
      const width = valueInRange(root, '[data-width]', profile.width, errors, 'width');
      const height = valueInRange(root, '[data-height]', profile.height, errors, 'height');
      if (profile.id === 'area-magnet-quantity-v1') {
        selected.widthCm = width;
        selected.heightCm = height;
      } else {
        selected.widthMm = width;
        selected.heightMm = height;
      }
      if (!Object.keys(errors).length && profile.id === 'area-magnet-quantity-v1') {
        const areaCm2 = selected.widthCm * selected.heightCm;
        const normalizedArea = areaCm2 / (profile.priceTable?.referenceAreaCm2 ?? 6200);
        const factor = (normalizedArea * (profile.priceTable?.linearFactor ?? 1.3)) -
          ((profile.priceTable?.quadraticFactor ?? 0.3) * Math.pow(normalizedArea, 2));
        priceCents = Math.round((basePriceCents * factor * quantity) + (profile.fixedSurchargeCents ?? 400));
      } else if (!Object.keys(errors).length) {
        priceCents = Math.round((quantity / (profile.priceTable?.batchSize ?? 25)) *
          (basePriceCents + ((selected.widthMm * selected.heightMm) * (profile.rateCents ?? 1))));
      }
    }
    if (!Object.keys(errors).length) {
      priceCents = Math.round(priceCents * (1 + (Number(profile.priceAdjustmentPercent || 0) / 100)));
    }
    if (!Object.keys(errors).length && priceCents <= 0) errors._price = 'Diese Kombination kann nicht berechnet werden.';
    return { valid: Object.keys(errors).length === 0, errors, priceCents, selection: selected };
  }

  function render(root, profile) {
    const result = calculate(root, profile);
    root.querySelectorAll('[data-error-for]').forEach((node) => {
      const message = result.errors[node.dataset.errorFor] || '';
      node.textContent = message;
      node.hidden = !message;
    });
    root.querySelector('[data-total]').textContent = result.valid
      ? window.SmagtronCalculatorCore.money(result.priceCents, 'EUR')
      : '–';
    root.querySelector('[data-submit]').disabled = !result.valid;
    return result;
  }

  async function requestQuote(input, fetchImpl) {
    const response = await fetchImpl('/apps/smagtron-calculator/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productId: input.productId, profileId: input.profileId, selection: input.selection })
    });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || `Quote fehlgeschlagen (${response.status})`);
    return payload;
  }

  async function addToCart(root, profile, result) {
    const submit = root.querySelector('[data-submit]');
    const status = root.querySelector('[data-status]');
    submit.disabled = true;
    submit.textContent = 'Wird hinzugefügt …';
    status.hidden = true;
    try {
      const quote = await requestQuote({
        productId: root.dataset.productId,
        profileId: profile.id,
        selection: result.selection
      }, window.fetch.bind(window));
      root.querySelector('[data-total]').textContent = window.SmagtronCalculatorCore.money(quote.priceCents, quote.currency);
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{
          id: Number(quote.variantId),
          quantity: 1,
          properties: { ...quote.properties, _Calculator: `Smagtron ${profile.id}` }
        }] })
      });
      if (!response.ok) throw new Error(`Shopify meldet HTTP ${response.status}`);
      window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      status.textContent = `Konfiguration konnte nicht hinzugefügt werden: ${error.message}`;
      status.hidden = false;
      submit.disabled = false;
      submit.textContent = 'In den Warenkorb';
    }
  }

  function init(root) {
    root.closest('product-info')?.classList.add('smagtron-product-info--calculator');
    let profile;
    try { profile = parseProfile(root); }
    catch {
      root.querySelector('[data-status]').textContent = 'Calculator-Konfiguration konnte nicht geladen werden.';
      root.querySelector('[data-status]').hidden = false;
      return;
    }
    root.querySelectorAll('input, select').forEach((input) => {
      input.addEventListener('input', () => render(root, profile));
      input.addEventListener('change', () => render(root, profile));
    });
    root.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      const result = render(root, profile);
      if (result.valid) addToCart(root, profile, result);
    });
    render(root, profile);
  }

  function initAll() {
    document.querySelectorAll('[data-smagtron-measurement-calculator]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  if (typeof module === 'object' && module.exports) module.exports = { requestQuote };
})();
